import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    useTheme,
    Snackbar,
    Alert,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, ArrowBack as ArrowBackIcon, ContentCopy as ContentCopyIcon, Info as InfoIcon } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ProjectSimulation as IProjectSimulation, ProjectPosition, MonthlyResult, CashFlowResult, Project, ProjectStatus } from '../../types/project';
import { projectService } from '../../services/projectService';
import { positionService, Position } from '../../services/positionService';
import { formatCurrency } from '../../utils/format';
import { alpha } from '@mui/material/styles';
import { useCLTCalculator } from '../../hooks/useCLTCalculator';
import { CLTCostDetails } from '../common/CLTCostDetails';
import { CLTCostBreakdown } from '../../services/cltCalculator';
import { SCPCostDetails } from '../common/SCPCostDetails';
import { SCPCostBreakdown } from '../../services/cltCalculator';
import { PJCostDetails } from '../common/PJCostDetails';
import { PJCostBreakdown } from '../../services/cltCalculator';
import { ContractCostHelp } from '../common/ContractCostHelp';
import { PositionStatus } from '../../types/project';

/** 'YYYY-MM-DD' completo e existente no calendário. */
const isValidDateString = (value: string) => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) return false;
    const [y, m, d] = value.split('-').map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
};

/**
 * Data de término (inclusiva) de um período de N meses, em UTC para não deslocar pelo fuso:
 * termina na véspera do mesmo dia N meses depois (01/10 + 6 meses = 31/03).
 * Quem começa no último dia do mês termina no último dia do mês final (31/10 + 1 = 30/11),
 * e dias inexistentes não "transbordam" para o mês seguinte (antes 31/10 + 1 dava 01/12).
 */
const computeEndDate =(start: string, months: number) => {
    const [y, m, d] = start.split('T')[0].split('-').map(Number);
    const lastDay = (year: number, monthIndex: number) => new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
    const targetMonth = m - 1 + months;
    if (d === lastDay(y, m - 1)) {
        return new Date(Date.UTC(y, targetMonth + 1, 0)).toISOString().split('T')[0];
    }
    const day = Math.min(d, lastDay(y, targetMonth));
    return new Date(Date.UTC(y, targetMonth, day - 1)).toISOString().split('T')[0];
};

interface MonthlyDREValue {
    value: number;
    bold?: boolean;
}

interface DRERowConfig {
    value: number;
    bold?: boolean;
    primary: boolean;
}

const ProjectSimulationComponent: React.FC = () => {
    const theme = useTheme();
    const { id, simulationId } = useParams<{ id: string; simulationId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [positions, setPositions] = useState<Position[]>([]);
    const cltCalculator = useCLTCalculator();
    const [selectedCLTPosition, setSelectedCLTPosition] = useState<{
        index: number;
        costs: CLTCostBreakdown;
    } | null>(null);
    const [selectedSCPPosition, setSelectedSCPPosition] = useState<{
        index: number;
        costs: SCPCostBreakdown;
    } | null>(null);
    const [selectedPJPosition, setSelectedPJPosition] = useState<{
        index: number;
        costs: PJCostBreakdown;
    } | null>(null);
    const [helpDialogOpen, setHelpDialogOpen] = useState(false);
    const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');

    // Constantes para os índices de encargos
    const ENCARGOS = {
        CLT: 1.00, // Será calculado pelo CLTCalculator
        PJ: 1.15,
        SCP: 1.30
    };
    
    const IMPOSTO_RATE = 0.0655; // 6.55%

    interface CashFlowEntry {
        month: string;
        inflow: number;
        outflow: number;
        initialBalance: number;
        balance: number;
        finalBalance: number;
    }

    interface MonthlyDREValues {
        revenue: number;
        taxes: number;
        netRevenue: number;
        costs: number;
        grossMargin: number;
        grossMarginPercentage: number;
        bench: number;
        projectMargin: number;
        projectMarginPercentage: number;
        headCount: number;
        realMonthlyCosts: number;
        realMonthlyBench: number;
    }

    interface CashFlowRow {
        label: string;
        getValue: (cf: CashFlowEntry) => number;
        highlight?: boolean;
        colorByValue?: boolean;
    }

    const [simulation, setSimulation] = useState<IProjectSimulation>({
        name: '',
        margin: 0,
        start_date: new Date().toISOString(),
        end_date: new Date().toISOString(),
        duration_months: 1,
        positions: [],
        monthly_results: '[]',
        cash_flow: '[]',
        total_revenue: 0,
        total_costs: 0,
        total_profit: 0,
        profit_margin: 0,
        is_approved: false
    });
    // Texto do campo de duração separado do valor numérico, para o usuário poder apagar e redigitar
    const [durationInput, setDurationInput] = useState('1');
    useEffect(() => {
        setDurationInput(String(simulation.duration_months));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [simulation.id]);
    const [monthlyResults, setMonthlyResults] = useState<MonthlyResult[]>([]);
    const [cashFlow, setCashFlow] = useState<CashFlowResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success' as 'success' | 'error'
    });

    useEffect(() => {
        loadPositions();
        if (id && simulationId && simulationId !== 'nova') {
            loadSimulation(Number(simulationId));
        } else {
            setLoading(false);
        }
    }, [id, simulationId]);

    const loadPositions = async () => {
        try {
            const data = await positionService.getPositions();
            setPositions(data);
        } catch (error) {
            console.error('Erro ao carregar posições:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao carregar posições',
                severity: 'error'
            });
        }
    };

    useEffect(() => {
        try {
            setMonthlyResults(JSON.parse(simulation.monthly_results));
            setCashFlow(JSON.parse(simulation.cash_flow));
        } catch (error) {
            console.error('Erro ao parsear resultados:', error);
            setMonthlyResults([]);
            setCashFlow([]);
        }
    }, [simulation.monthly_results, simulation.cash_flow]);

    const loadSimulation = async (simId?: number) => {
        try {
            setLoading(true);
            if (simId) {
                const data = await projectService.getProjectSimulation(Number(id), simId);
                const positionsWithCost = data.positions.map(pos => ({
                    ...pos,
                    cost: calculatePositionCost(pos)
                }));
                setSimulation({
                    ...data,
                    positions: positionsWithCost
                });
            }
        } catch (error) {
            console.error('Erro ao carregar simulação:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao carregar simulação',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    // Efeito para calcular valores quando a simulação é carregada ou alterada
    useEffect(() => {
        if (!loading && simulation.positions.length > 0) {
            const monthlyDRE = calculateMonthlyDRE();
            
            // Calcula os totais considerando os períodos corretos
            const totalRevenue = monthlyDRE.revenue * simulation.duration_months;
            const totalNetRevenue = monthlyDRE.netRevenue * simulation.duration_months;
            const totalCosts = monthlyDRE.costs * simulation.duration_months;
            const totalBench = monthlyDRE.bench * simulation.duration_months;
            const totalProfit = monthlyDRE.projectMargin * simulation.duration_months;
            
            // Calcula a margem bruta do projeto (sobre receita líquida)
            const projectMarginPercentage = (totalProfit / totalNetRevenue) * 100;
            
            setSimulation(prev => ({
                ...prev,
                total_revenue: totalRevenue,
                total_costs: totalCosts + totalBench,
                total_profit: totalProfit,
                profit_margin: projectMarginPercentage // Margem bruta do projeto (sobre receita líquida)
            }));
        }
    }, [loading, simulation.positions, simulation.margin, simulation.duration_months]);

    const handleBack = () => {
        navigate(`/gestor/projetos/${id}`);
    };

    const handleAddPosition = () => {
        const newPosition: ProjectPosition = {
            id: 0,
            project_id: Number(id),
            role: '',
            contract_type: 'CLT',
            base_salary: 0,
            start_date: simulation.start_date.split('T')[0],
            end_date: simulation.end_date.split('T')[0],
            allocation: 100,
            margin: 0,
            duration_months: simulation.duration_months,
            status: PositionStatus.PLANNED,
            position_type: 'cost'
        };

        setSimulation(prev => ({
            ...prev,
            positions: [...prev.positions, newPosition]
        }));
    };

    const calculatePositionCost = (position: ProjectPosition): number => {
        const monthlyCost = position.base_salary * (position.allocation / 100);
        return position.contract_type === 'CLT' ? monthlyCost * 1.8 : monthlyCost;
    };

    const handlePositionChange = (index: number, field: keyof ProjectPosition, value: any) => {
        const newPositions = [...simulation.positions];
        
        if (field === 'start_date' || field === 'duration_months') {
            // Para datas, vamos garantir que sempre seja no primeiro dia do mês
            const startDate = field === 'start_date'
                ? value + '-01' // Adiciona o dia 01 ao mês/ano selecionado
                : String(newPositions[index].start_date).split('T')[0];
            const durationMonths = field === 'duration_months' ? value : newPositions[index].duration_months;

            // Valores intermediários (campo vazio enquanto digita) não podem gerar datas inválidas
            if (!isValidDateString(startDate) || !Number.isInteger(durationMonths) || durationMonths < 1) {
                return;
            }

            newPositions[index] = {
                ...newPositions[index],
                [field]: field === 'start_date' ? startDate : value,
                end_date: computeEndDate(startDate, durationMonths)
            };
        } else if (field === 'position_type') {
            newPositions[index] = {
                ...newPositions[index],
                position_type: value as 'cost' | 'bench'
            };
        } else {
            newPositions[index] = {
                ...newPositions[index],
                [field]: value
            };
        }

        // Atualiza o totalCost
        newPositions[index].cost = calculatePositionCost(newPositions[index]);

        setSimulation(prev => ({
            ...prev,
            positions: newPositions
        }));
    };

    const handleDeletePosition = (index: number) => {
        setSimulation(prev => ({
            ...prev,
            positions: prev.positions.filter((_, i) => i !== index)
        }));
    };

    const handleDuplicatePosition = (index: number) => {
        const positionToDuplicate = simulation.positions[index];
        const duplicatedPosition: ProjectPosition = {
            ...positionToDuplicate,
            id: 0,
            status: PositionStatus.PLANNED
        };

        setSimulation(prev => ({
            ...prev,
            positions: [...prev.positions, duplicatedPosition]
        }));
    };

    const handleSimulationChange = (field: keyof IProjectSimulation, value: any) => {
        setSimulation(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSave = async () => {
        try {
            let savedSimulation;

            // Cria um tipo que omite o id para novas posições
            type PositionForBackend = Omit<ProjectPosition, 'id' | 'cost'> & { id?: number };
            type SimulationForBackend = Omit<IProjectSimulation, 'positions'> & { positions: PositionForBackend[] };

            const simulationData: SimulationForBackend = {
                ...simulation,
                id: simulationId !== 'nova' ? Number(simulationId) : undefined,
                monthly_results: JSON.stringify(monthlyResults),
                cash_flow: JSON.stringify(cashFlow),
                start_date: simulation.start_date.split('T')[0],
                end_date: simulation.end_date.split('T')[0],
                duration_months: simulation.duration_months,
                positions: simulation.positions.map(position => {
                    // Remove o campo cost que é calculado
                    const { cost, ...positionData } = position;
                    
                    // Para novas posições, remove o id
                    if (position.id === 0) {
                        const { id, ...newPosition } = positionData;
                        return {
                            ...newPosition,
                            start_date: position.start_date.split('T')[0],
                            end_date: position.end_date.split('T')[0]
                        };
                    }
                    
                    // Para posições existentes, mantém o id
                    return {
                        ...positionData,
                        start_date: position.start_date.split('T')[0],
                        end_date: position.end_date.split('T')[0]
                    };
                })
            };

            if (simulationId && simulationId !== 'nova') {
                savedSimulation = await projectService.updateProjectSimulation(Number(id), simulationData as IProjectSimulation);
            } else {
                savedSimulation = await projectService.createProjectSimulation(Number(id), simulationData as IProjectSimulation);
            }

            // Se for uma nova simulação e for salva com sucesso, seleciona ela automaticamente
            if (simulationId === 'nova' && savedSimulation.id) {
                await projectService.selectProjectSimulation(Number(id), savedSimulation.id);
            }

            setSnackbar({
                open: true,
                message: 'Simulação salva com sucesso!',
                severity: 'success'
            });
            navigate(`/gestor/projetos/${id}`);
        } catch (error) {
            console.error('Erro ao salvar simulação:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao salvar simulação. Tente novamente.',
                severity: 'error'
            });
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Função auxiliar para gerar array de meses
    const generateMonths = (startDate: string, durationMonths: number) => {
        const months = [];
        const start = new Date(startDate);
        
        for (let i = 0; i < durationMonths; i++) {
            const date = new Date(start);
            date.setMonth(start.getMonth() + i);
            months.push(date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }));
        }
        
        return months;
    };

    // Função para calcular valores mensais do DRE
    const calculateMonthlyDRE = (): MonthlyDREValues => {
        // Calcula o custo total de uma posição considerando sua duração específica
        const calculatePositionCost = (position: ProjectPosition) => {
            if (position.contract_type === 'CLT') {
                const cltCost = cltCalculator.calculateCosts(position.base_salary);
                return cltCost.custoTotal * (position.allocation / 100) * position.duration_months;
            }
            if (position.contract_type === 'SCP') {
                const scpCost = cltCalculator.calculateSCPCosts(position.base_salary);
                return scpCost.custoTotal * (position.allocation / 100) * position.duration_months;
            }
            if (position.contract_type === 'PJ') {
                const pjCost = cltCalculator.calculatePJCosts(position.base_salary);
                return pjCost.custoTotal * (position.allocation / 100) * position.duration_months;
            }
            const encargo = ENCARGOS[position.contract_type as keyof typeof ENCARGOS] || 1;
            const custoMensal = position.base_salary * (position.allocation / 100) * encargo;
            return custoMensal * position.duration_months;
        };

        // Calcula custos mensais separados por tipo (custo/bench)
        const costPositions = simulation.positions.filter(p => p.position_type === 'cost');
        const benchPositions = simulation.positions.filter(p => p.position_type === 'bench');
        
        // Calcula o custo total de cada tipo de posição
        const totalCostPositions = costPositions.reduce((total, pos) => total + calculatePositionCost(pos), 0);
        const totalBenchPositions = benchPositions.reduce((total, pos) => total + calculatePositionCost(pos), 0);

        // Encontra a maior duração entre todas as posições
        const maxDuration = Math.max(
            simulation.duration_months,
            ...simulation.positions.map(p => p.duration_months)
        );

        // Calcula os custos médios mensais distribuídos pelo período do projeto
        const projectMonthlyCosts = totalCostPositions / simulation.duration_months;
        const projectMonthlyBench = totalBenchPositions / simulation.duration_months;
        const totalMonthlyCosts = projectMonthlyCosts + projectMonthlyBench;

        // Calcula a receita mensal necessária para atingir a margem desejada (sobre faturamento bruto)
        const targetMargin = simulation.margin / 100;
        const monthlyRevenue = totalMonthlyCosts / (1 - targetMargin - IMPOSTO_RATE);
        const monthlyTaxes = monthlyRevenue * IMPOSTO_RATE;
        const monthlyNetRevenue = monthlyRevenue - monthlyTaxes;

        // Calcula margens
        const monthlyGrossMargin = monthlyNetRevenue - projectMonthlyCosts;
        const monthlyProjectMargin = monthlyGrossMargin - projectMonthlyBench;

        // Calcula percentuais em relação à receita líquida para Margem Bruta I e II
        const grossMarginPercentage = (monthlyGrossMargin / monthlyNetRevenue) * 100;
        const projectMarginPercentage = (monthlyProjectMargin / monthlyNetRevenue) * 100;

        return {
            revenue: monthlyRevenue,
            taxes: monthlyTaxes,
            netRevenue: monthlyNetRevenue,
            costs: projectMonthlyCosts,
            grossMargin: monthlyGrossMargin,
            grossMarginPercentage,
            bench: projectMonthlyBench,
            projectMargin: monthlyProjectMargin,
            projectMarginPercentage,
            headCount: costPositions.length + benchPositions.length,
            realMonthlyCosts: totalCostPositions / maxDuration,
            realMonthlyBench: totalBenchPositions / maxDuration
        };
    };

    // Função para formatar moeda com suporte a números negativos
    const formatCurrencyWithNegative = (value: number) => {
        return formatCurrency(value);
    };

    // Função para calcular fluxo de caixa mensal
    const calculateMonthlyCashFlow = (): CashFlowEntry[] => {
        const monthlyDRE = calculateMonthlyDRE();
        
        // Encontra a maior duração entre todas as posições
        const maxDuration = Math.max(
            simulation.duration_months + 1, // +1 para incluir o último mês de faturamento
            ...simulation.positions.map(p => p.duration_months)
        );

        const result: CashFlowEntry[] = [];
        let previousBalance = 0;
        
        // Função para calcular o custo total de uma posição em um mês específico
        const getPositionCostForMonth = (position: ProjectPosition, monthIndex: number) => {
            const startDate = new Date(position.start_date);
            const positionStartMonth = startDate.getMonth() + startDate.getFullYear() * 12;
            const simulationStartDate = new Date(simulation.start_date);
            const simulationStartMonth = simulationStartDate.getMonth() + simulationStartDate.getFullYear() * 12;
            const monthDiff = monthIndex + (simulationStartMonth - positionStartMonth);

            if (monthDiff >= 0 && monthDiff < position.duration_months) {
                const encargo = ENCARGOS[position.contract_type as keyof typeof ENCARGOS] || 1;
                return position.base_salary * (position.allocation / 100) * encargo;
            }
            return 0;
        };

        // Gera meses até a maior duração
        for (let i = 0; i < maxDuration; i++) {
            const date = new Date(simulation.start_date);
            date.setMonth(date.getMonth() + i);
            const month = date.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' });

            // Calcula custos do mês atual
            const monthCosts = simulation.positions.reduce((total, position) => {
                return total + getPositionCostForMonth(position, i);
            }, 0);

            // Faturamento começa no mês 2 e vai até o mês duration_months + 1
            const inflow = (i >= 1 && i <= simulation.duration_months) ? monthlyDRE.netRevenue : 0;
            const outflow = monthCosts;
            
            const balance = inflow - outflow;
            const finalBalance = previousBalance + balance;
            
            result.push({
                month,
                inflow,
                outflow,
                initialBalance: previousBalance,
                balance,
                finalBalance
            });

            previousBalance = finalBalance;
        }

        return result;
    };

    // Função para formatar data como jan/25
    const formatDateToMonthYear = (dateString: string) => {
        const date = new Date(dateString);
        const month = date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
        const year = date.getFullYear().toString().slice(2);
        return `${month}/${year}`;
    };

    // Função para formatar data como YYYY-MM para o input month
    const formatDateToYearMonth = (dateString: string) => {
        return dateString.split('T')[0].substring(0, 7); // Retorna YYYY-MM
    };

    const CustomDateInput = React.forwardRef<HTMLInputElement, {
        value: string;
        onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
        disabled?: boolean;
    }>(({ value, onChange, disabled }, ref) => {
        const displayValue = formatDateToMonthYear(value);
        const inputValue = formatDateToYearMonth(value);

        return (
            <TextField
                type="month"
                size="small"
                fullWidth
                value={inputValue}
                onChange={onChange}
                disabled={disabled}
                inputRef={ref}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                    style: { 
                        textAlign: 'center',
                        fontSize: '0.875rem'
                    }
                }}
                sx={{
                    '& .MuiInputBase-input': { 
                        py: 0.5
                    },
                    '& .MuiInputBase-input:not([value=""]):not(:focus)': {
                        color: 'transparent'
                    },
                    position: 'relative',
                    '&::after': {
                        content: `"${displayValue}"`,
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        pointerEvents: 'none',
                        fontSize: '0.875rem',
                        color: disabled ? 'rgba(0, 0, 0, 0.87)' : 'inherit'
                    }
                }}
            />
        );
    });

    const handleShowCLTDetails = (index: number) => {
        const position = simulation.positions[index];
        if (position.contract_type === 'CLT') {
            const costs = cltCalculator.calculateCosts(position.base_salary);
            setSelectedCLTPosition({ index, costs });
        }
    };

    const handleShowSCPDetails = (index: number) => {
        const position = simulation.positions[index];
        if (position.contract_type === 'SCP') {
            const costs = cltCalculator.calculateSCPCosts(position.base_salary);
            setSelectedSCPPosition({ index, costs });
        }
    };

    const handleShowPJDetails = (index: number) => {
        const position = simulation.positions[index];
        if (position.contract_type === 'PJ') {
            const costs = cltCalculator.calculatePJCosts(position.base_salary);
            setSelectedPJPosition({ index, costs });
        }
    };

    const handleRejectSimulation = async () => {
        try {
            if (!id || !simulationId || simulationId === 'nova') return;
            
            await projectService.rejectProjectSimulation(Number(id), Number(simulationId), rejectionReason);
            setSnackbar({
                open: true,
                message: 'Simulação rejeitada com sucesso',
                severity: 'success'
            });
            navigate(`/gestor/projetos/${id}`);
        } catch (error) {
            console.error('Erro ao rejeitar simulação:', error);
            setSnackbar({
                open: true,
                message: 'Erro ao rejeitar simulação',
                severity: 'error'
            });
        }
        setRejectionDialogOpen(false);
    };

    if (loading) {
        return (
            <Box p={3}>
                <Typography>Carregando simulação...</Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={handleBack} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                <Typography variant="h4">
                    {t('manager.simulation.title')}
                </Typography>
                </Box>
                <Box display="flex" gap={2}>
                    <Tooltip title="Ver explicação dos cálculos">
                        <IconButton
                            onClick={() => setHelpDialogOpen(true)}
                            color="primary"
                        >
                            <InfoIcon />
                        </IconButton>
                    </Tooltip>
                    {id && simulationId && simulationId !== 'nova' && (
                        <Button
                            variant="outlined"
                            color="error"
                            onClick={() => setRejectionDialogOpen(true)}
                        >
                            Voltar para Oportunidade
                        </Button>
                    )}
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                >
                    Salvar
                </Button>
                </Box>
            </Box>

            {/* Informações Básicas */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        label="Nome da Simulação"
                        value={simulation.name}
                        onChange={(e) => handleSimulationChange('name', e.target.value)}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Margem Geral (%)"
                        value={simulation.margin}
                        onChange={(e) => handleSimulationChange('margin', Number(e.target.value))}
                        InputProps={{ inputProps: { min: 0, max: 100 } }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Data de Início"
                        value={simulation.start_date.split('T')[0]}
                        onChange={(e) => {
                            // Enquanto o usuário digita, o campo passa por valores vazios/incompletos:
                            // só recalcula com uma data válida (antes: RangeError e a tela quebrava)
                            if (!isValidDateString(e.target.value)) return;
                            handleSimulationChange('start_date', e.target.value);
                            handleSimulationChange('end_date', computeEndDate(e.target.value, simulation.duration_months));
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="number"
                        label="Duração (meses)"
                        value={durationInput}
                        onChange={(e) => {
                            // Permite apagar e redigitar; só aplica durações inteiras >= 1
                            setDurationInput(e.target.value);
                            const duration = Number(e.target.value);
                            if (!Number.isInteger(duration) || duration < 1) return;
                            handleSimulationChange('duration_months', duration);
                            handleSimulationChange('end_date', computeEndDate(simulation.start_date, duration));
                        }}
                        onBlur={() => setDurationInput(String(simulation.duration_months))}
                        error={durationInput !== '' && (!Number.isInteger(Number(durationInput)) || Number(durationInput) < 1)}
                        helperText={durationInput === '' || Number(durationInput) < 1 ? 'Mínimo de 1 mês' : ' '}
                        InputProps={{ inputProps: { min: 1 } }}
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <TextField
                        fullWidth
                        type="date"
                        label="Data de Término"
                        value={simulation.end_date.split('T')[0]}
                        disabled
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            '& .MuiInputBase-input.Mui-disabled': {
                                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                                color: 'rgba(0, 0, 0, 0.87)'
                            }
                        }}
                    />
                </Grid>
            </Grid>

            {/* Resumo Financeiro */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                {t('manager.simulation.summary.totalRevenue')}
                            </Typography>
                            <Typography variant="h5">
                                {formatCurrency(simulation.total_revenue)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                {t('manager.simulation.summary.totalCost')}
                            </Typography>
                            <Typography variant="h5">
                                {formatCurrency(simulation.total_costs)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                {t('manager.simulation.summary.totalProfit')}
                            </Typography>
                            <Typography variant="h5" color={theme.palette.success.main}>
                                {formatCurrency(simulation.total_profit)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                {t('manager.simulation.summary.profitMargin')}
                            </Typography>
                            <Typography variant="h5">
                                {simulation.profit_margin.toFixed(2)}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Posições */}
            <Box mb={4}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="h6">
                        {t('manager.simulation.positions.title')}
                    </Typography>
                    <Button
                        startIcon={<AddIcon />}
                        onClick={handleAddPosition}
                        size="small"
                    >
                        Nova Posição
                    </Button>
                </Box>

                <TableContainer component={Paper}>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell width="17%">
                                    <Tooltip title="Cargo">
                                        <span>Cargo</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="9%" align="center">
                                    <Tooltip title="Tipo de Contrato">
                                        <span>Contr.</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="12%" align="center">
                                    <Tooltip title="Salário Base">
                                        <span>Salário</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="13%" align="center">
                                    <Tooltip title="Data de Início">
                                        <span>Início</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="9%" align="center">
                                    <Tooltip title="Duração em Meses">
                                        <span>Dur.</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="12%" align="center">
                                    <Tooltip title="Data de Término">
                                        <span>Fim</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="10%" align="center">
                                    <Tooltip title="Percentual de Alocação">
                                        <span>Aloc.</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="12%" align="center">
                                    <Tooltip title="Tipo da Posição">
                                        <span>Tipo</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell width="6%" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {simulation.positions.map((position, index) => (
                                <TableRow key={index}>
                                    <TableCell>
                                        <FormControl fullWidth size="small">
                                            <Select
                                            value={position.role}
                                            onChange={(e) => handlePositionChange(index, 'role', e.target.value)}
                                                sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                                            >
                                                {positions.map((pos) => (
                                                    <MenuItem key={pos.id} value={pos.name}>
                                                        {pos.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="center">
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={position.contract_type}
                                                onChange={(e) => handlePositionChange(index, 'contract_type', e.target.value)}
                                                sx={{ 
                                                    '& .MuiSelect-select': { 
                                                        py: 0.5,
                                                        pr: '24px',
                                                        pl: '24px',
                                                        minWidth: 0,
                                                        fontSize: '0.875rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '2px'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="CLT" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>CLT</MenuItem>
                                                <MenuItem value="PJ" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>PJ</MenuItem>
                                                <MenuItem value="SCP" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>SCP</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                        <TextField
                                            type="number"
                                                value={position.base_salary}
                                                onChange={(e) => handlePositionChange(index, 'base_salary', Number(e.target.value))}
                                                InputProps={{
                                                    startAdornment: <span style={{ marginRight: '2px', fontSize: '0.8rem' }}>R$</span>
                                                }}
                                                sx={{ 
                                                    width: '85%',
                                                    '& .MuiInputBase-input': { 
                                                        py: 0.5,
                                                        fontSize: '0.875rem',
                                                        textAlign: 'center',
                                                        pr: '4px'
                                                    }
                                                }}
                                            />
                                            {position.contract_type === 'CLT' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                            size="small"
                                                        onClick={() => handleShowCLTDetails(index)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {position.contract_type === 'SCP' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowSCPDetails(index)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {position.contract_type === 'PJ' && (
                                                <Tooltip title="Ver detalhes">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => handleShowPJDetails(index)}
                                                    >
                                                        <InfoIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell align="center">
                                        <CustomDateInput
                                            value={position.start_date}
                                            onChange={(e) => handlePositionChange(index, 'start_date', e.target.value)}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={position.duration_months}
                                            onChange={(e) => handlePositionChange(index, 'duration_months', Number(e.target.value))}
                                            inputProps={{ 
                                                min: 1, 
                                                style: { 
                                                    textAlign: 'center',
                                                    paddingRight: '4px'
                                                } 
                                            }}
                                            InputProps={{
                                                endAdornment: <span style={{ 
                                                    marginLeft: '2px', 
                                                    fontSize: '0.8rem',
                                                    position: 'absolute',
                                                    right: '4px'
                                                }}>m</span>
                                            }}
                                            sx={{ 
                                                '& .MuiInputBase-input': { 
                                                    py: 0.5,
                                                    fontSize: '0.875rem'
                                                },
                                                '& .MuiInputBase-root': {
                                                    paddingRight: '16px'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <CustomDateInput
                                            value={position.end_date}
                                            onChange={(e) => handlePositionChange(index, 'end_date', e.target.value)}
                                            disabled
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField
                                            type="number"
                                            size="small"
                                            fullWidth
                                            value={position.allocation}
                                            onChange={(e) => handlePositionChange(index, 'allocation', Number(e.target.value))}
                                            InputProps={{ 
                                                inputProps: { 
                                                    min: 0, 
                                                    max: 100, 
                                                    style: { 
                                                        textAlign: 'center',
                                                        paddingRight: '4px'
                                                    } 
                                                },
                                                endAdornment: <span style={{ 
                                                    marginLeft: '2px', 
                                                    fontSize: '0.8rem',
                                                    position: 'absolute',
                                                    right: '4px'
                                                }}>%</span>
                                            }}
                                            sx={{ 
                                                '& .MuiInputBase-input': { 
                                                    py: 0.5,
                                                    fontSize: '0.875rem'
                                                },
                                                '& .MuiInputBase-root': {
                                                    paddingRight: '16px'
                                                }
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <FormControl fullWidth size="small">
                                            <Select
                                                value={position.position_type || 'cost'}
                                                onChange={(e) => handlePositionChange(index, 'position_type', e.target.value)}
                                                sx={{ 
                                                    '& .MuiSelect-select': { 
                                                        py: 0.5,
                                                        pr: '24px',
                                                        pl: '24px',
                                                        minWidth: 0,
                                                        fontSize: '0.875rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    },
                                                    '& .MuiSelect-icon': {
                                                        right: '2px'
                                                    }
                                                }}
                                            >
                                                <MenuItem value="cost" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>Custo</MenuItem>
                                                <MenuItem value="bench" sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>Bench</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={0.5} justifyContent="flex-end">
                                            <Tooltip title="Duplicar posição">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleDuplicatePosition(index)}
                                                    color="primary"
                                                    sx={{ padding: 0.5 }}
                                                >
                                                    <ContentCopyIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Excluir posição">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeletePosition(index)}
                                            color="error"
                                                    sx={{ padding: 0.5 }}
                                        >
                                                    <DeleteIcon fontSize="small" />
                                        </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* DRE Consolidado */}
            <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    DRE Consolidado
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>DRE Projeto</TableCell>
                                <TableCell align="right">Total (R$)</TableCell>
                                <TableCell align="right">Total (R$/mês)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                const monthlyDRE = calculateMonthlyDRE();
                                const totalMonths = simulation.duration_months;

                                const rows = [
                                    {
                                        label: 'Faturamento (R$)',
                                        total: monthlyDRE.revenue * totalMonths,
                                        monthly: monthlyDRE.revenue,
                                        type: 'primary',
                                        bold: true
                                    },
                                    {
                                        label: 'Impostos (R$)',
                                        total: monthlyDRE.taxes * totalMonths,
                                        monthly: monthlyDRE.taxes,
                                        type: 'secondary'
                                    },
                                    {
                                        label: 'Receita Líquida (R$)',
                                        total: monthlyDRE.netRevenue * totalMonths,
                                        monthly: monthlyDRE.netRevenue,
                                        type: 'primary',
                                        bold: true
                                    },
                                    {
                                        label: 'Custo Folha (R$)',
                                        total: monthlyDRE.costs * totalMonths,
                                        monthly: monthlyDRE.costs,
                                        type: 'secondary'
                                    },
                                    {
                                        label: 'Margem Bruta I (R$)',
                                        total: monthlyDRE.grossMargin * totalMonths,
                                        monthly: monthlyDRE.grossMargin,
                                        type: 'primary',
                                        bold: true
                                    },
                                    {
                                        label: 'Mg Bruta I (%)',
                                        total: monthlyDRE.grossMarginPercentage,
                                        monthly: monthlyDRE.grossMarginPercentage,
                                        isPercentage: true,
                                        type: 'secondary'
                                    },
                                    {
                                        label: 'Bench (R$)',
                                        total: monthlyDRE.bench * totalMonths,
                                        monthly: monthlyDRE.bench,
                                        type: 'secondary'
                                    },
                                    {
                                        label: 'Margem Projeto (R$)',
                                        total: monthlyDRE.projectMargin * totalMonths,
                                        monthly: monthlyDRE.projectMargin,
                                        type: 'primary',
                                        bold: true
                                    },
                                    {
                                        label: 'Mg Bruto Projeto (%)',
                                        total: monthlyDRE.projectMarginPercentage,
                                        monthly: monthlyDRE.projectMarginPercentage,
                                        isPercentage: true,
                                        type: 'secondary'
                                    }
                                ];

                                return rows.map((row, index) => (
                                    <TableRow 
                                        key={index} 
                                        sx={{ 
                                            backgroundColor: row.type === 'primary' 
                                                ? alpha(theme.palette.primary.light, 0.05)
                                                : 'inherit',
                                            '&:hover': {
                                                backgroundColor: alpha(theme.palette.primary.light, 0.1)
                                            },
                                            '& td': {
                                                fontWeight: row.bold ? 'bold' : 400,
                                                color: row.type === 'primary' 
                                                    ? theme.palette.text.primary
                                                    : theme.palette.text.secondary
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            {row.label}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.isPercentage 
                                                ? `${row.total.toFixed(1)}%`
                                                : formatCurrency(row.total)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {row.isPercentage 
                                                ? `${row.monthly.toFixed(1)}%`                                                : formatCurrency(row.monthly)}
                                        </TableCell>
                                </TableRow>
                                ));
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* DRE Mensal */}
            <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    DRE Mensal
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>DRE - R$</TableCell>
                                <TableCell align="right">Total do Projeto</TableCell>
                                {generateMonths(simulation.start_date, simulation.duration_months).map((month) => (
                                    <TableCell key={month} align="right">{month}</TableCell>
                                ))}
                                </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                const monthlyDRE = calculateMonthlyDRE();
                                const monthlyValues: Record<string, DRERowConfig> = {
                                    'Faturamento': {
                                        value: monthlyDRE.revenue,
                                        bold: true,
                                        primary: true
                                    },
                                    'Impostos': {
                                        value: monthlyDRE.taxes,
                                        primary: false
                                    },
                                    'Receita Líquida': {
                                        value: monthlyDRE.netRevenue,
                                        bold: true,
                                        primary: true
                                    },
                                    'Custo Folha': {
                                        value: monthlyDRE.costs,
                                        primary: false
                                    },
                                    'Margem Bruta I': {
                                        value: monthlyDRE.grossMargin,
                                        bold: true,
                                        primary: true
                                    },
                                    'Mg Bruta I (%)': {
                                        value: monthlyDRE.grossMarginPercentage,
                                        primary: false
                                    },
                                    'Bench': {
                                        value: monthlyDRE.bench,
                                        primary: false
                                    },
                                    'Margem Projeto (R$)': {
                                        value: monthlyDRE.projectMargin,
                                        bold: true,
                                        primary: true
                                    },
                                    'Mg Bruto Projeto (%)': {
                                        value: monthlyDRE.projectMarginPercentage,
                                        primary: false
                                    },
                                    'Head Count': {
                                        value: monthlyDRE.headCount,
                                        primary: false
                                    }
                                };

                                return Object.entries(monthlyValues).map(([label, config]) => (
                                    <TableRow key={label} sx={{
                                        backgroundColor: config.primary
                                            ? alpha(theme.palette.primary.light, 0.05)
                                            : 'inherit',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.light, 0.1)
                                        },
                                        '& td': {
                                            fontWeight: config.bold ? 'bold' : 400,
                                            color: config.primary
                                                ? theme.palette.text.primary
                                                : theme.palette.text.secondary
                                        }
                                    }}>
                                        <TableCell>{label}</TableCell>
                                        <TableCell align="right">
                                            {label.includes('%')
                                                ? `${config.value.toFixed(1)}%`
                                                : label === 'Head Count'
                                                    ? config.value.toFixed(1)
                                                    : formatCurrency(config.value * simulation.duration_months)}
                                        </TableCell>
                                        {Array(simulation.duration_months).fill(null).map((_, i) => (
                                            <TableCell key={i} align="right">
                                                {label.includes('%')
                                                    ? `${config.value.toFixed(1)}%`
                                                    : label === 'Head Count'
                                                        ? config.value.toFixed(1)
                                                        : formatCurrency(config.value)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ));
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Fluxo de Caixa */}
            <Box mb={4}>
                <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
                    Fluxo de Caixa
                </Typography>
                <TableContainer component={Paper}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>FC do Projeto</TableCell>
                                {generateMonths(simulation.start_date, Math.max(
                                    simulation.duration_months + 1,
                                    ...simulation.positions.map(p => p.duration_months)
                                )).map((month) => (
                                    <TableCell key={month} align="right">{month}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(() => {
                                const rows: CashFlowRow[] = [
                                    {
                                        label: 'FCO',
                                        getValue: (cf: CashFlowEntry) => cf.balance,
                                        highlight: true,
                                        colorByValue: true
                                    },
                                    {
                                        label: 'Entradas Padrão',
                                        getValue: (cf: CashFlowEntry) => cf.inflow
                                    },
                                    {
                                        label: 'Saídas',
                                        getValue: (cf: CashFlowEntry) => cf.outflow
                                    },
                                    {
                                        label: 'Caixa Inicial',
                                        getValue: (cf: CashFlowEntry) => cf.initialBalance
                                    },
                                    {
                                        label: 'Caixa Final',
                                        getValue: (cf: CashFlowEntry) => cf.finalBalance,
                                        highlight: true,
                                        colorByValue: true
                                    }
                                ];

                                const cashFlowData = calculateMonthlyCashFlow();

                                return rows.map((row) => (
                                    <TableRow key={row.label} sx={{
                                        backgroundColor: row.highlight
                                            ? alpha(theme.palette.primary.light, 0.05)
                                            : 'inherit',
                                        '&:hover': {
                                            backgroundColor: alpha(theme.palette.primary.light, 0.1)
                                        }
                                    }}>
                                        <TableCell sx={{ 
                                            fontWeight: row.colorByValue ? 'bold' : 'inherit'
                                        }}>
                                            {row.label}
                                        </TableCell>
                                        {cashFlowData.map((cf, index) => {
                                            const value = row.getValue(cf);
                                            let textColor = 'inherit';
                                            
                                            if (row.colorByValue) {
                                                // Apenas para FCO e Caixa Final
                                                textColor = value < 0 ? theme.palette.error.main : theme.palette.success.main;
                                            }

                                            return (
                                                <TableCell 
                                                    key={index} 
                                                    align="right"
                                                    sx={{ 
                                                        color: textColor,
                                                        fontWeight: row.colorByValue ? 'bold' : 'inherit'
                                                    }}
                                                >
                                                    {formatCurrencyWithNegative(value)}
                                                </TableCell>
                                            );
                                        })}
                                </TableRow>
                                ));
                            })()}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* Rejection Dialog */}
            <Dialog
                open={rejectionDialogOpen}
                onClose={() => setRejectionDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Voltar para Oportunidade
                </DialogTitle>
                <DialogContent>
                    <Typography gutterBottom>
                        Por favor, explique o motivo pelo qual esta simulação não foi aprovada:
                    </Typography>
                    <TextField
                        autoFocus
                        multiline
                        rows={4}
                        fullWidth
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Digite aqui o motivo da rejeição..."
                        sx={{ mt: 2 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRejectionDialogOpen(false)}>
                        Cancelar
                    </Button>
                    <Button 
                        onClick={handleRejectSimulation}
                        color="error"
                        disabled={!rejectionReason.trim()}
                    >
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Snackbar at the end of the component */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            {selectedCLTPosition && (
                <CLTCostDetails
                    open={!!selectedCLTPosition}
                    onClose={() => setSelectedCLTPosition(null)}
                    costBreakdown={selectedCLTPosition.costs}
                />
            )}
            {selectedSCPPosition && (
                <SCPCostDetails
                    open={!!selectedSCPPosition}
                    onClose={() => setSelectedSCPPosition(null)}
                    costBreakdown={selectedSCPPosition.costs}
                />
            )}
            {selectedPJPosition && (
                <PJCostDetails
                    open={!!selectedPJPosition}
                    onClose={() => setSelectedPJPosition(null)}
                    costBreakdown={selectedPJPosition.costs}
                />
            )}
            <ContractCostHelp
                open={helpDialogOpen}
                onClose={() => setHelpDialogOpen(false)}
            />
        </Box>
    );
};

export default ProjectSimulationComponent;
